import usePosTypes from '../hooks/usePosTypes.js';

// Tone comes from the user-managed pos_types table (Settings -> POS Types).
// Any value not found there - e.g. a legacy/removed type still tagged on a
// record - falls back to grey so it always renders.
export function PosBadge({ value }) {
  const types = usePosTypes();
  if (!value) return null;
  const match = types.find((t) => t.name === value);
  const tone = match?.tone || 'grey';
  return <Badge tone={tone}>{value}</Badge>;
}

export function StatusBadge({ value }) {
  if (!value) return null;
  const v = String(value).toLowerCase();
  let tone = 'grey';
  if (v === 'active' || v === 'complete' || v === 'received') tone = 'green';
  else if (v === 'pending') tone = 'amber';
  else if (v === 'inactive' || v === 'closed') tone = 'grey';
  return <Badge tone={tone}>{value}</Badge>;
}

// Tone map for the canonical Lead status set Dave locked in 2026-05-28.
// Anything not in this map (e.g. legacy values not yet normalised) falls
// through to grey so the badge still renders without throwing.
const LEAD_STATUS_TONES = {
  'Website Lead':    'teal',    // fresh inbound off the website form
  'Cold prospect':   'grey',    // dormant, no recent activity
  'Active lead':     'blue',    // being worked
  'Demo scheduled':  'purple',  // demo on the calendar
  'Quote sent':      'amber',   // waiting on client response
  'Negotiating':     'orange',  // back-and-forth in progress
  'Won':             'green',
  'Lost':            'red',
  'Not interested':  'red',
  'Former Customer': 'amber'
};

export function LeadStatusBadge({ value }) {
  if (!value) return null;
  const tone = LEAD_STATUS_TONES[value] || 'grey';
  return <Badge tone={tone}>{value}</Badge>;
}

// Renewal urgency badge used by the Customers list "Renewal Status"
// column. Maps the three buckets from subscriptionsController
// (Overdue / Near / Active) to the standard status palette: red for
// overdue, amber for renewal within 180 days, green when comfortably
// out, grey when there's no renewal date on file.
export function RenewalStatusBadge({ value }) {
  if (!value) return null;
  const v = String(value).toLowerCase();
  let tone = 'grey';
  if (v === 'overdue')     tone = 'red';
  else if (v === 'near')   tone = 'amber';
  else if (v === 'active') tone = 'green';
  return <Badge tone={tone}>{value}</Badge>;
}

// Shows where a document is in the open-tracking lifecycle. Pluralises
// "1 time" / "N times". Used on the Documents tab of Customer/Lead
// detail pages and on the dashboard's Quotes Awaiting Response panel.
export function OpenStatusBadge({ document: d }) {
  if (!d) return null;
  if (!d.sent_at) return <Badge tone="grey">Not sent</Badge>;
  const count = d.open_count || 0;
  if (count === 0) return <Badge tone="grey">Not yet opened</Badge>;
  const label = count === 1 ? 'Opened 1 time' : `Opened ${count} times`;
  return <Badge tone="green">{label}</Badge>;
}

export default function Badge({ tone = 'grey', children }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}
